/**
 * The "nothing in this order ships" state, in ONE place.
 *
 * `createOrderInTx` stamps `metadata.virtual_only = true` when EVERY line of an
 * order is a virtual product (a course, a service, a virtual kit). It is
 * server-authoritative — set from the same `allLinesVirtual` the payment-method
 * and shipping guards read, never from client input — and several surfaces key
 * behaviour off it: the virtual auto-complete, the virtual dimension of
 * `kit_auto_create_slot`, and the unpaid-order payment lock.
 *
 * Each of those was reading `metadata.virtual_only` with its own inline cast.
 * This is the same argument `manual-shipping.ts` makes for `isAwaitingShippingFee`:
 * a predicate shared across surfaces is what stops them drifting, and the cast
 * is easy to get subtly wrong (`=== true` vs truthy matters — `metadata` is
 * free-form jsonb, so a stray `"false"` string is truthy).
 *
 * Structurally typed and null-tolerant on purpose, so it reads identically on
 * the API's Drizzle row and on the SPA's `OrderRow` DTO.
 */

export type VirtualOrderMetadata = {
  virtual_only?: unknown;
};

/**
 * Is every line of this order virtual — i.e. is there nothing to ship?
 *
 * False for a MIXED order: `createOrderInTx` only stamps the flag when the whole
 * order is virtual, which is why a mixed cart still collects an address.
 */
export function isVirtualOnlyOrder(
  order: { metadata?: VirtualOrderMetadata | null } | null | undefined,
): boolean {
  return order?.metadata?.virtual_only === true;
}
